import { useApiMutation } from './useApiMutation';
import { useApiQuery } from './useApiQuery';
import { queryKeys } from './queryKeys';
import { ledgerQueryKeys } from './useGeneralLedger';
import { managementFeeService } from '@/services/managementFeeService';
import type {
  ManagementFeeSchedule,
  ManagementFeeScheduleCreate,
  ManagementFeeSchedulePage,
  ManagementFeeScheduleUpdate,
  ManagementFeeSettlement,
} from '@/types/management-fees';
import { APIError } from '@/utils/api-client';

export const useManagementFees = (
  fundId: string,
  params?: { is_active?: boolean; page?: number; page_size?: number }
) => {
  return useApiQuery<ManagementFeeSchedulePage>(
    queryKeys.managementFees.list(fundId, params),
    () => managementFeeService.list(fundId, params),
    {
      enabled: !!fundId,
    }
  );
};

export const useCreateManagementFee = (fundId: string) => {
  return useApiMutation<
    ManagementFeeSchedule,
    APIError,
    ManagementFeeScheduleCreate
  >((data) => managementFeeService.create(fundId, data), {
    invalidateKeys: [
      queryKeys.managementFees.list(fundId),
      queryKeys.managementFees.summary(fundId),
    ],
    successMessage: 'Management fee schedule added successfully',
  });
};

export const useUpdateManagementFee = (fundId: string) => {
  return useApiMutation<
    ManagementFeeSchedule,
    APIError,
    { scheduleId: string; data: ManagementFeeScheduleUpdate }
  >(
    ({ scheduleId, data }) =>
      managementFeeService.update(fundId, scheduleId, data),
    {
      invalidateKeys: [
        queryKeys.managementFees.list(fundId),
        queryKeys.managementFees.summary(fundId),
      ],
      successMessage: 'Management fee schedule updated successfully',
    }
  );
};

export const useDeleteManagementFee = (fundId: string) => {
  return useApiMutation<void, APIError, string>(
    (scheduleId) => managementFeeService.delete(fundId, scheduleId),
    {
      invalidateKeys: [
        queryKeys.managementFees.list(fundId),
        queryKeys.managementFees.summary(fundId),
      ],
      successMessage: 'Management fee schedule deleted successfully',
    }
  );
};

export const useSettleManagementFeeThrough = (
  fundId: string,
  paymentId: string
) => {
  return useApiMutation<{ id: string }, APIError, ManagementFeeSettlement>(
    (settledThrough) =>
      managementFeeService.settleThrough(fundId, paymentId, settledThrough),
    {
      invalidateKeys: [
        queryKeys.fundTransactions.detail(fundId, paymentId),
        queryKeys.fundTransactions.reconciliation(fundId, paymentId),
        ledgerQueryKeys.all,
      ],
      successMessage: 'Settlement updated',
    }
  );
};
